import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Shared report exporters: CSV, Excel, and PDF. Framework-agnostic (no React),
 * so they add nothing to the render path and cannot affect the app's behavior.
 * Print is handled in the page with window.print() and a print stylesheet.
 */

export type Cell = string | number;

function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportCsv(filename: string, headers: string[], rows: Cell[][]): void {
  const esc = (s: Cell) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))].join('\r\n');
  download(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
}

export function exportXlsx(filename: string, headers: string[], rows: Cell[][]): void {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, filename);
}

export interface Sheet {
  name: string;
  headers: string[];
  rows: Cell[][];
}

/** Excel workbook with one tab per sheet — e.g. a Summary tab and a Line Items
 *  tab. Sheet names are trimmed to Excel's 31-char limit. */
export function exportXlsxSheets(filename: string, sheets: Sheet[]): void {
  const wb = XLSX.utils.book_new();
  sheets.forEach((s, i) => {
    const ws = XLSX.utils.aoa_to_sheet([s.headers, ...s.rows]);
    const name = (s.name || `Sheet${i + 1}`).slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  XLSX.writeFile(wb, filename);
}

/** Single CSV file holding several titled sections stacked vertically (each
 *  with its own header row), separated by a blank line. */
export function exportCsvSections(
  filename: string,
  sections: { title?: string; headers: string[]; rows: Cell[][] }[]
): void {
  const esc = (s: Cell) => `"${String(s ?? '').replace(/"/g, '""')}"`;
  const lines: string[] = [];
  sections.forEach((sec, i) => {
    if (i > 0) lines.push('');
    if (sec.title) lines.push(esc(sec.title));
    lines.push(sec.headers.map(esc).join(','));
    sec.rows.forEach((r) => lines.push(r.map(esc).join(',')));
  });
  download(new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' }), filename);
}

export interface ChartImage {
  url: string; // PNG data URL
  aspect: number; // height / width
}

/**
 * Rasterise an on-page SVG chart to a PNG data URL so it can be embedded in a
 * PDF. Inline SVG only (no external refs), so the canvas is never tainted.
 */
export function svgToPng(svg: SVGSVGElement): Promise<ChartImage> {
  return new Promise((resolve, reject) => {
    const vb = svg.viewBox && svg.viewBox.baseVal;
    const w = (vb && vb.width) || svg.clientWidth || 800;
    const h = (vb && vb.height) || svg.clientHeight || 300;
    const xml = new XMLSerializer().serializeToString(svg);
    const src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w * 2;
      canvas.height = h * 2;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('no canvas context'));
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve({ url: canvas.toDataURL('image/png'), aspect: h / w });
    };
    img.onerror = () => reject(new Error('svg image load failed'));
    img.src = src;
  });
}

export function exportPdf(
  title: string,
  filename: string,
  headers: string[],
  rows: Cell[][],
  chart?: ChartImage
): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt' });
  doc.setFontSize(14);
  doc.text(title, 40, 40);
  let startY = 56;
  if (chart) {
    const pageW = doc.internal.pageSize.getWidth();
    const imgW = pageW - 80;
    const imgH = imgW * chart.aspect;
    doc.addImage(chart.url, 'PNG', 40, startY, imgW, imgH);
    startY += imgH + 20;
  }
  autoTable(doc, {
    head: [headers],
    body: rows.map((r) => r.map((c) => String(c ?? ''))),
    startY,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [25, 118, 210] },
  });
  doc.save(filename);
}
